import _AlertWarningFilledIcon from "@adsk/alloy-react-icon/es/lib/AlertWarningFilledIcon";
import _CheckmarkCircleFilledIcon from "@adsk/alloy-react-icon/es/lib/CheckmarkCircleFilledIcon";
import _styled, { default as _styled2 } from "styled-components";
const _excluded = ["className", "style", "name", "error", "alertMessage", "metadata", "variant", "children", "renderIcon", "renderFileName", "renderMetadata", "renderMainAction", "renderSecondaryAction"];
function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
function _objectWithoutProperties(e, t) { if (null == e) return {}; var o, r, i = _objectWithoutPropertiesLoose(e, t); if (Object.getOwnPropertySymbols) { var s = Object.getOwnPropertySymbols(e); for (r = 0; r < s.length; r++) o = s[r], t.includes(o) || {}.propertyIsEnumerable.call(e, o) && (i[o] = e[o]); } return i; }
function _objectWithoutPropertiesLoose(r, e) { if (null == r) return {}; var t = {}; for (var n in r) if ({}.hasOwnProperty.call(r, n)) { if (e.includes(n)) continue; t[n] = r[n]; } return t; }
import React from 'react';
import PropTypes from 'prop-types';
import styledC from 'styled-components';
import { useDeprecate } from '@adsk/alloy-react-helpers';
import ProgressRing from '@adsk/alloy-react-progress-ring';
import theme, { stylePropType } from '@adsk/alloy-react-theme';
import Tooltip from '@adsk/alloy-react-tooltip';
import { FILE_HANDLER_VARIANTS, variantPropType } from './consts';
import Metadata from './layout/Metadata';
import Name from './layout/Name';
const Action = styledC.div.withConfig({
  displayName: "FileHandler__Action",
  componentId: "sc-16emb0w-0"
})({
  height: 24,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  flexShrink: 0
});

/**
 * A FileHandler
 * docs.
 */
const FileHandler = /*#__PURE__*/React.forwardRef((_ref, ref) => {
  let {
      className,
      style,
      name,
      error,
      alertMessage,
      metadata,
      variant = FILE_HANDLER_VARIANTS.DEFAULT,
      children,
      renderIcon,
      renderFileName = props => /*#__PURE__*/React.createElement(Name, props),
      renderMetadata = props => /*#__PURE__*/React.createElement(Metadata, props),
      renderMainAction,
      renderSecondaryAction
    } = _ref,
    rest = _objectWithoutProperties(_ref, _excluded);
  useDeprecate(!!error, 'FileHandler', 'error', 'alertMessage');
  const finalVariant = variant || FILE_HANDLER_VARIANTS.DEFAULT;
  const disabled = [FILE_HANDLER_VARIANTS.LOADING, FILE_HANDLER_VARIANTS.REMOVED].includes(finalVariant);
  const showAlert = [FILE_HANDLER_VARIANTS.ERROR, FILE_HANDLER_VARIANTS.WARNING].includes(finalVariant);
  return /*#__PURE__*/React.createElement(_StyledDiv, _extends({}, rest, {
    ref: ref,
    className: className,
    $_css: [{
      padding: "".concat(theme.spacing.S, "px ").concat(theme.spacing.M, "px"),
      flex: 1,
      display: 'flex',
      boxShadow: "0px 1px 0px 0px ".concat(theme.tokens.colors.border.subtle.value)
    }, style]
  }), renderIcon === null || renderIcon === void 0 ? void 0 : renderIcon({
    style: {
      marginRight: theme.spacing.XS,
      flexShrink: 0
    },
    color: disabled ? theme.tokens.colors.text.inactive.value : theme.tokens.colors.text.primary.value
  }), /*#__PURE__*/React.createElement(_StyledDiv2, {
    $_css2: [theme.typography.ellipsis, {
      flex: 1
    }]
  }, renderFileName({
    disabled,
    variant: finalVariant,
    children: name
  }), renderMetadata({
    disabled,
    children: metadata
  })), [FILE_HANDLER_VARIANTS.DEFAULT, FILE_HANDLER_VARIANTS.ERROR, FILE_HANDLER_VARIANTS.WARNING].includes(finalVariant) && renderMainAction && /*#__PURE__*/React.createElement(Action, {
    style: {
      marginLeft: theme.spacing.M
    }
  }, renderMainAction()), [FILE_HANDLER_VARIANTS.LOADING, FILE_HANDLER_VARIANTS.SUCCESS, FILE_HANDLER_VARIANTS.ERROR, FILE_HANDLER_VARIANTS.WARNING].includes(finalVariant) && /*#__PURE__*/React.createElement(Action, {
    style: {
      marginLeft: theme.spacing.XS
    }
  }, finalVariant === FILE_HANDLER_VARIANTS.LOADING && /*#__PURE__*/React.createElement(ProgressRing, {
    size: ProgressRing.SIZES.XSMALL,
    "data-testid": "FileHandlerLoader"
  }), finalVariant === FILE_HANDLER_VARIANTS.SUCCESS && /*#__PURE__*/React.createElement(_CheckmarkCircleFilledIcon, {
    color: theme.tokens.colors.icon.success.value
  }), showAlert && /*#__PURE__*/React.createElement(Tooltip, {
    content: alertMessage || error,
    renderTarget: props => /*#__PURE__*/React.createElement(Tooltip.Target, _extends({}, props, {
      shouldWrapChildren: false
    }))
  }, /*#__PURE__*/React.createElement(_AlertWarningFilledIcon, {
    color: finalVariant === FILE_HANDLER_VARIANTS.ERROR ? theme.tokens.colors.icon.error.value : theme.tokens.colors.icon.warning.value
  }))), renderSecondaryAction && /*#__PURE__*/React.createElement(Action, {
    style: {
      marginLeft: theme.spacing.M
    }
  }, renderSecondaryAction()));
});
FileHandler.displayName = 'FileHandler';
FileHandler.propTypes = {
  /** Styles applied to the root element */
  style: stylePropType,
  /** Class applied to the root element */
  className: PropTypes.string,
  /** Content */
  children: PropTypes.any,
  /** The style variant */
  variant: variantPropType,
  /** File name */
  name: PropTypes.string.isRequired,
  /** Message to display in error or warning tooltip */
  alertMessage: PropTypes.string,
  /** File name */
  metadata: PropTypes.string,
  /** Icon type */
  renderIcon: PropTypes.func,
  /** Render custom name component */
  renderFileName: PropTypes.func,
  /** Render main action */
  renderMainAction: PropTypes.func,
  /** Render secondary action */
  renderSecondaryAction: PropTypes.func
};
export default Object.assign(FileHandler, {
  VARIANTS: FILE_HANDLER_VARIANTS
});
var _StyledDiv = styledC("div").withConfig({
  displayName: "FileHandler___StyledDiv",
  componentId: "sc-16emb0w-1"
})(["", ""], p => p.$_css);
var _StyledDiv2 = styledC("div").withConfig({
  displayName: "FileHandler___StyledDiv2",
  componentId: "sc-16emb0w-2"
})(["", ""], p => p.$_css2);
//# sourceMappingURL=FileHandler.js.map